import { createFeatureSelector, createSelector } from '@ngrx/store';
import { ManagerCustomerUser, CustomerManagerCompany, ManagerCompanyUser, UserInvitation } from '@model';
import { ManagersState } from '@appStore/managers';
import { sortByProperty } from '@utils/sx-utilities';
import { MeteringPointsState } from '@appStore/metering-points';
import { CustomersState } from '@appStore/customers';

import { ConfigurationState } from '@appStore/configuration';
import { RoleType } from '@enums';

const getManagerFeatureState = createFeatureSelector<ManagersState>('managers');

const getManagerCustomerState = createSelector(
  getManagerFeatureState,
  (state: ManagersState) => state.managerCustomers
);
const getManagerCompanyInvitationState = createSelector(
  getManagerFeatureState,
  (state: ManagersState) => state.managerCompanyInvitations
);
const getManagerCompaniesState = createSelector(
  getManagerFeatureState,
  (state: ManagersState) => state.managerCompanies
);
const getCustomerFeatureState = createFeatureSelector<CustomersState>('customers');

const getCustomerState = createSelector(
  getCustomerFeatureState,
  (state: CustomersState) => state.customers
);

const getMeteringPointFeatureState = createFeatureSelector<MeteringPointsState>('meteringpoints');
const getMeteringPointsState = createSelector(
  getMeteringPointFeatureState,
  state => state.meteringPoints
);

const getMeteringPointGroupState = createSelector(
  getMeteringPointFeatureState,
  state => state.meteringPointGroups
)

const getConfigurationFeatureState = createFeatureSelector<ConfigurationState>('configuration');
const getPermissionsLoadedState = createSelector(
  getConfigurationFeatureState,
  (state: ConfigurationState) => state.configuration
);

export const getCustomersAndSelected = createSelector(
  getManagerCompaniesState,
  getManagerCustomerState,
  (mccState, selected) => {
    return {
      customers: mccState.customers,
      selected: selected.selected,
      usersLoaded: selected.loaded || selected.loading
    };
  }
);

export const getBulkInvitationCompleted = createSelector(
  getManagerCompanyInvitationState,
  getManagerCustomerState,
  (mciState, mcState) => {
    return mciState.created && mcState.usersAdded;
  }
);

export const getAggregatedInvitationsLoaded = createSelector(
  getManagerCompanyInvitationState,
  mciState => {
    return mciState.loaded;
  }
);

export const getAggregatedInvitations = createSelector(
  getManagerCompanyInvitationState,
  mciState => {
    const invitations = mciState.data;

    if (invitations && invitations.length > 0) {
      const aggregatedInvitations = [] as UserInvitation[];

      invitations.forEach(invitation => {
        let existing = aggregatedInvitations.some(x => x.email === invitation.email && x.managerCompanyId === invitation.managerCompanyId);

        if (existing) {
          // Update existing - Add customerId to customerIds...
          let aggregatedInvitation = aggregatedInvitations.find(
            x => x.email === invitation.email && x.managerCompanyId === invitation.managerCompanyId
          );

          if (!aggregatedInvitation.customerIds.some(cid =>  invitation.customerId === cid)) {
            aggregatedInvitation.customerIds.push(invitation.customerId);
            aggregatedInvitation.customers = 'Visa kunder';
          }
        } else {
          // Add new
          const customerIds = [] as string[];
          if (invitation.customerId) {
            customerIds.push(invitation.customerId);
          }

          const data: UserInvitation = {
            ...invitation,
            customerIds: customerIds,
            customers: customerIds.length > 0 ? 'Visa kunder' : ''
          };
  
          aggregatedInvitations.push(data);
        }
      });
      return sortByProperty(aggregatedInvitations, 'email');
    } else {
      return [];
    }
  }
);

export const getCustomersAndSelection = createSelector(
  getManagerCompaniesState,
  getManagerCustomerState,
  (mccState, selection) => {
    return {
      customers: mccState.customers,
      selection: selection.selectedCustomers,
      usersLoaded: selection.selectedCustomersUsersLoaded || selection.selectedCustomersUsersLoading
    };
  }
);

export const getCompanyAndSelected = createSelector(
  getManagerCompaniesState,
  mccState => {
    return {
      companies: mccState.data,
      selected: mccState.selected
    };
  }
);

export const getManagerCompanyAndUserSelected = createSelector(
  getManagerCompaniesState,
  mccState => {
    return {
      selectedUser: mccState.selectedUser,
      selected: mccState.selected,
      userCustomers: mccState.userCustomers
    };
  }
);

export const getFilledManagerCustomerUsersLoaded = createSelector(
  getCustomerState,
  getManagerCustomerState,
  getMeteringPointsState,
  (cState, cuState, mpState) => {
    return cState.loaded && cuState.loaded && mpState.loaded;
  }
);

export const getFilledCustomerManagerCompanyLoaded = createSelector(
  getCustomerState,
  getManagerCustomerState,
  getManagerCompaniesState,
  getMeteringPointsState,
  (cState, sState, mccState, mpState) => {
    return cState.loaded && sState.loaded && mccState.customersLoaded && mpState.loaded;
  }
);

export const getFilledCustomerManagerCompaniesLoaded = createSelector(
  getCustomerState,
  getManagerCompaniesState,
  getMeteringPointsState,
  (cState, mccState, mpState) => {
    return cState.loaded && mccState.customersLoaded && mpState.loaded;
  }
);

export const getFilledManagerCustomersLoaded = createSelector(
  getCustomerState,
  getManagerCompaniesState,
  getMeteringPointsState,
  (cState, mccState, mpState) => {
    return cState.loaded && mccState.customersLoaded && mpState.loaded;
  }
);

export const getFilledManagerCustomersSelectedLoaded = createSelector(
  getCustomerState,
  getManagerCompaniesState,
  getMeteringPointsState,
  (cState, mccState, mpState) => {
    return cState.loaded && mccState.customersLoaded && mpState.loaded;
  }
);

export const getFilledManagerCompaniesUsersLoaded = createSelector(
  getManagerCompaniesState,
  mcState => {
    return mcState.usersLockStatusLoaded && mcState.usersLoaded;
  }
);

export const getFilledManagerCustomersSelected = createSelector(
  getCustomerState,
  getManagerCompaniesState,
  getMeteringPointsState,
  (cState, mccState, mpState) => {
    const customers = cState.data;
    const managerCompanyCustomers = mccState.customers;
    const meteringPoints = mpState.data;
    const selectedCustomers = mccState.selectedCustomers;

    if (managerCompanyCustomers.length > 0 && managerCompanyCustomers.length > 0) {
      const filledCustomerManagerCompanies = [] as CustomerManagerCompany[];

      managerCompanyCustomers.forEach(cmCompany => {
        if (!selectedCustomers.some(x => x === cmCompany.customerId)) {
          return;
        }
        let selectedCustomer = customers.find(c => c.id === cmCompany.customerId);

        const data: CustomerManagerCompany = {
          id: cmCompany.id,
          customerId: cmCompany.customerId,
          customerName: selectedCustomer ? selectedCustomer.name : '',
          organisationNumber: cmCompany.organisationNumber,
          managerCompany: cmCompany.managerCompany,
          managerCompanyId: cmCompany.managerCompanyId,
          meteringPoints: cmCompany.meteringPoints.map(mp => {
            let meteringpoint = meteringPoints.find(m => m.id === mp.id);
            if (meteringpoint) {
              return { ...mp, name: meteringpoint.name };
            }
            return { ...mp, name: '' };
          })
        };

        filledCustomerManagerCompanies.push(data);
      });
      return sortByProperty(filledCustomerManagerCompanies, 'customerId');
    } else {
      return [];
    }
  }
);

export const getFilledManagerCompaniesUsers = createSelector(
  getManagerCompaniesState,
  mcState => {
    const managerUsers = mcState.users;
    const lockedManagerUsers = mcState.usersLockStatus;

    if (managerUsers.length > 0 && managerUsers.length > 0) {
      const filledManagerUsers = [] as ManagerCompanyUser[];

      managerUsers.forEach(user => {
        let isLocked = false;

        if (lockedManagerUsers && lockedManagerUsers.length > 0) {
          let locked = lockedManagerUsers.filter(
            x => x.userManagerId === user.id && x.managerCompanyId === user.managerCompanyId
          );

          if (locked && locked.length > 0) {
            isLocked = locked.some(x => x.isLocked);
          }
        }
        const userData: ManagerCompanyUser = {
          id: user.id,
          userId: user.userId,
          name: user.name,
          email: user.email,
          phoneNumber: user.phoneNumber,
          roleTypes: user.roleTypes,
          managerCompanyId: user.managerCompanyId,
          hasAccessToAllMeteringPoints: user.hasAccessToAllMeteringPoints,
          roleType: user.roleType,
          selected: user.selected,
          isLocked: isLocked,
          isPrimaryContactPerson : user.isPrimaryContactPerson
        };

        filledManagerUsers.push(userData);
      });
      return sortByProperty(filledManagerUsers, 'name');
    } else {
      return [];
    }
  }
);

export const getFilledManagerCustomers = createSelector(
  getCustomerState,
  getManagerCompaniesState,
  getMeteringPointsState,
  (cState, mccState, mpState) => {
    const customers = cState.data;
    const managerCompanyCustomers = mccState.customers;
    const meteringPoints = mpState.data;

    if (managerCompanyCustomers.length > 0 && managerCompanyCustomers.length > 0) {
      const filledCustomerManagerCompanies = [] as CustomerManagerCompany[];

      managerCompanyCustomers.forEach(cmCompany => {
        let selectedCustomer = customers.find(c => c.id === cmCompany.customerId);

        const data: CustomerManagerCompany = {
          id: cmCompany.id,
          customerId: cmCompany.customerId,
          customerName: selectedCustomer ? selectedCustomer.name : '',
          organisationNumber: cmCompany.organisationNumber,
          managerCompany: cmCompany.managerCompany,
          managerCompanyId: cmCompany.managerCompanyId,
          meteringPoints: cmCompany.meteringPoints.map(mp => {
            let meteringpoint = meteringPoints.find(m => m.id === mp.id);
            if (meteringpoint) {
              return { ...mp, name: meteringpoint.name };
            }
            return { ...mp, name: '' };
          })
        };

        filledCustomerManagerCompanies.push(data);
      });
      return sortByProperty(filledCustomerManagerCompanies, 'customerId');
    } else {
      return [];
    }
  }
);

export const getFilledCustomerManagerCompany = createSelector(
  getCustomerState,
  getManagerCustomerState,
  getManagerCompaniesState,
  getMeteringPointsState,
  (cState, sState, mccState, mpState) => {
    const customers = cState.data;
    const selectedManagerCustomer = sState.selected;
    const managerCompanyCustomers = mccState.customers;
    const meteringPoints = mpState.data;

    if (managerCompanyCustomers.length > 0 && managerCompanyCustomers.length > 0) {
      const customerManagerCompany = managerCompanyCustomers.find(
        cmCompany =>
          cmCompany.customerId === selectedManagerCustomer.customerId &&
          cmCompany.managerCompanyId === selectedManagerCustomer.managerCompanyId
      );

      let selectedCustomer = customers.find(c => c.id === selectedManagerCustomer.customerId);

      const data: CustomerManagerCompany = {
        id: customerManagerCompany.id,
        customerId: customerManagerCompany.customerId,
        customerName: selectedCustomer ? selectedCustomer.name : '',
        organisationNumber: selectedManagerCustomer.organisationNumber,
        managerCompany: customerManagerCompany.managerCompany,
        managerCompanyId: customerManagerCompany.managerCompanyId,
        meteringPoints: customerManagerCompany.meteringPoints.map(mp => {
          let meteringpoint = meteringPoints.find(m => m.id === mp.id);
          if (meteringpoint) {
            return { ...mp, name: meteringpoint.name, meteringType: meteringpoint.meteringType };
          }
          return { ...mp, name: '' };
        })
      };

      return data;
    } else {
      return null;
    }
  }
);

export const getFilledCustomerManagerCompanies = createSelector(
  getCustomerState,
  getManagerCompaniesState,
  getMeteringPointsState,
  (cState, mccState, mpState) => {
    const customers = cState.data;
    const customerManagerCompanies = mccState.customers;
    const meteringPoints = mpState.data;

    if (customerManagerCompanies.length > 0 && customerManagerCompanies.length > 0) {
      const filledCustomerManagerCompanies = [] as CustomerManagerCompany[];

      customerManagerCompanies.forEach(cmCompany => {
        let selectedCustomer = customers.find(c => c.id === cmCompany.customerId);

        const data: CustomerManagerCompany = {
          id: cmCompany.id,
          customerId: cmCompany.customerId,
          customerName: selectedCustomer ? selectedCustomer.name : '',
          organisationNumber: cmCompany.organisationNumber,
          managerCompany: cmCompany.managerCompany,
          managerCompanyId: cmCompany.managerCompanyId,
          meteringPoints: cmCompany.meteringPoints.map(mp => {
            let meteringpoint = meteringPoints.find(m => m.id === mp.id);
            if (meteringpoint) {
              return { ...mp, name: meteringpoint.name };
            }
            return { ...mp, name: '' };
          })
        };

        filledCustomerManagerCompanies.push(data);
      });
      return sortByProperty(filledCustomerManagerCompanies, 'organisationNumber');
    } else {
      return [];
    }
  }
);

export const getFilledManagerCustomerUsers = createSelector(
  getCustomerState,
  getManagerCustomerState,
  getMeteringPointsState,
  getManagerCompaniesState,
  (cState, cuState, mpState, mcState) => {
    const customers = cState.data;
    const customersUsers = cuState.data;
    const meteringPoints = mpState.data;
    const managerUsers = mcState.users;
    const lockedManagerUsers = mcState.usersLockStatus;

    if (customersUsers.length > 0 && customers.length > 0) {
      const filledCustomersUsers = [] as ManagerCustomerUser[];

      customersUsers.forEach(user => {
        let cust = customers.filter(x => x.id === user.customerId);
        let customerName = '';
        if (cust && cust.length > 0) {
          customerName = cust[0].name;
        }

        let managerUser = managerUsers.filter(
          x => x.userId === user.userId && x.managerCompanyId === user.managerCompanyId
        );
        let managerRoleTypes = [];
        let managerRoleType = RoleType.UnknownRoleType;
        let isLocked = false;

        if (managerUser && managerUser.length > 0) {
          managerRoleTypes = managerUser[0].roleTypes;
          managerRoleType = managerRoleTypes.reduce((max, p) => (p > max ? p : max), managerRoleTypes[0]);

          if (lockedManagerUsers && lockedManagerUsers.length > 0) {
            let locked = lockedManagerUsers.filter(
              x => x.userManagerId === managerUser[0].id && x.managerCompanyId === user.managerCompanyId
            );

            if (locked && locked.length > 0) {
              isLocked = locked.some(x => x.isLocked);
            }
          }
        }

        const userData: ManagerCustomerUser = {
          userId: user.userId,
          customerId: user.customerId,
          name: user.name,
          email: user.email,
          clientId: user.clientId,
          isPrimaryContactPerson: user.isPrimaryContactPerson,
          roleTypes: user.roleTypes,
          managerCompanyId: user.managerCompanyId,
          customerName: customerName,
          managerRoleType: managerRoleType,
          hasAccessToAllMeteringPoints: user.hasAccessToAllMeteringPoints,
          isLocked: isLocked,
          meteringPoints: user.meteringPoints.map(mp => {
            let meteringpoint = meteringPoints.find(m => m.id === mp.id);
            if (meteringpoint) {
              return { ...mp, name: meteringpoint.name };
            }
            return { ...mp, name: '' };
          })
        };

        filledCustomersUsers.push(userData);
      });
      return sortByProperty(filledCustomersUsers, 'name');
    } else {
      return [];
    }
  }
);

export const getReloadPermissionsLoaded = createSelector(
  getCustomerState,
  getMeteringPointsState,
  getMeteringPointGroupState,
  getPermissionsLoadedState,
  (cState, mpState, mpGroupState, plState) => {
    return cState.loaded && mpState.loaded && mpGroupState.loaded && plState.permissionsLoaded;
  }
);